import React, { useEffect, useState, ChangeEvent } from 'react';
import { Col, Row, Form, FormGroup, Label, Input, Button } from 'reactstrap';
import { post } from 'services/ApiService';
import InputError from '../../components/InputError';
import Required from '../../components/Required';
import AlertModal from '../../components/AlertModal';
import ConfirmModal from '../../components/ConfirmModal';
import { useAlertPlus, useConfirm } from 'hooks';
import { useUser } from 'context/UserContext';
import { getPractices } from 'features/practices/practicesAPI';
import { InsurerPractices } from 'types/Request';
import moment from 'moment';

interface Authorization {
  insurer_id: number;
  recipient_id: string;
  practice_id: number;
  practice_text: string;
  sessions: number;
}

export default function OSPTV() {
  // Autorizacion
  const [formData, setFormData] = useState<Authorization>({
    insurer_id: 75,
    recipient_id: '',
    practice_id: 7,
    practice_text: 'Modulo Consultorio',
    sessions: 5,
  });

  const { getMatricula } = useUser();
  const matriculateId = getMatricula();

  const [prestaciones, setPrestaciones] = useState<InsurerPractices>({
    modules: [],
    additionals: [],
  });

  const [formValidation, setFormValidation] = useState<Partial<Record<keyof typeof formData, boolean>>>({});

  const [authorization, setAuthorization] = useState<string>();

  const [alertShow, alertMessage, alertCallback, setAlert] = useAlertPlus('');
  const [isLoading, setLoading] = useState(true);
  const [confirm, confirmTitle, confirmAction, setConfirm] = useConfirm(false);

  if (!matriculateId) {
    return null;
  }

  const handleFormValidation = () => {
    let newFormValidation = {};

    let toValidate = Array.from(document.getElementsByClassName('form-control')) as HTMLInputElement[];
    toValidate = toValidate.filter(e => !e.disabled && e.required && e.type !== 'file');

    let missing = false;
    // Validar campos required
    toValidate.forEach(field => {
      if (field.required && !field.disabled && !field.value?.trim()) {
        field.classList.add('is-invalid');

        missing = true;
      } else {
        field.classList.remove('is-invalid');
      }
    });

    // Validar file inputs required
    toValidate = Array.from(document.getElementsByClassName('custom-file-input')) as HTMLInputElement[];
    toValidate.forEach(field => {
      if (field.required && !field.disabled) {
        field.classList.add('is-invalid');

        missing = true;
      }
    });

    // Validar cantidad de sesiones
    if (formData.sessions < 0 || formData.sessions > 5) {
      newFormValidation = {
        ...newFormValidation,
        sessions: false,
      };

      missing = true;
    }

    if (missing) {
      setFormValidation(newFormValidation);
      setAlert('Todos los campos marcados con * son obligatorios. ' +
        'Por favor complete los faltantes antes de enviar la solicitud.');
      return false;
    }

    return true;
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { target } = event;

    const name = target.name;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const extra: Partial<typeof formData> = {};

    // Si cambiaron los id de práctica, recuperamos el nuevo nombre
    switch (name) {
      case 'practice_id':
        if (typeof value === 'string') {
          extra['practice_text'] = prestaciones.modules.find(p => p.id === parseInt(value))!.name;
        } else {
          extra['practice_text'] = undefined;
        }
        break;
    }

    // Guardamos el nuevo valor
    setFormData({
      ...formData,
      ...extra,
      [name]: value,
    });
  };

  const handleAuthorization = async () => {
    setLoading(true);

    if (!handleFormValidation()) {
      setLoading(false);
      return;
    }

    if (!Object.values(formValidation).every(v => v)) {
      console.info('OSPTV', 'Error de validación');
      setAlert('Se encontraron errores en los datos ingresados. Por favor, solucione los mismos antes de enviar' +
        'el pedido de autorización.');
      setLoading(false);
      return;
    }

    const response = await post('insurers/authorize', formData );

    // Errores
    if (response.status === 'error' && response.code !== 'AUTHORIZATION_ERROR') {
      console.error('OSPTV', 'Se produjo un error al intentar realizar la autorización');
      console.info('OSPTV', response);
      if (response.message) {
        setAlert(response.message);
      } else {
        setAlert('Hubo un problema al intentar autorizar la práctica. Por favor, intente nuevamente más tarde');
      }

      setLoading(false);
      return;
    }

    // Rechazos
    if (response.code === 'AUTHORIZATION_ERROR') {
      if (response.message) {
        setAlert(response.message);
      } else {
        console.error('OSPTV', 'Práctica no autorizada por motivo desconocido');
        console.info('OSPTV', response);
        setAlert('No se autorizó la práctica');
      }

      setLoading(false);
      return;
    }

    setAuthorization(response.authorization_id);
  };

  useEffect(() => {
    getPractices({
      insurerId: formData.insurer_id,
      matriculateId: parseInt(matriculateId),
      date: moment().format('YYYY-MM-DD'),
    })
      .then(data => {
        if (!data) {
          throw new Error('Hay problemas de conexión con el sistema de Facturación. ' +
            'Temporalmente no se podrá enviar la solicitud.');
        }

        setPrestaciones({
          modules: data.modules ?? [],
          additionals: data.additionals ?? [],
        });
      })
      .catch(err => {
        setAlert(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  return (
    <>
      <Form onKeyPress={ e => e.key === 'Enter' && handleAuthorization() }>
        <FormGroup>
          <Row>
            <Col sm={6}>
              <Label for="numero_afiliado">Número de Beneficiario <Required /></Label>
              <Input
                id="recipient_id"
                name="recipient_id"
                type="text"
                size={10}
                maxLength={10}
                invalid={ formValidation.recipient_id === false }
                onChange={ handleInputChange }
                disabled={ isLoading }
                value={ formData.recipient_id }
                required
              />
              {
                formValidation.recipient_id === false
                  ? <InputError message="El número de afiliado ingresado no es válido" />
                  : null
              }
              <small className="text-muted">
                Se deben ingresar todos los numeros que figuran en la credencial sin espacios o barras
              </small>
            </Col>
          </Row>
        </FormGroup>
        <FormGroup>
          <Row>
            <Col sm={6}>
              <Label for="practice_id">Práctica <Required /></Label>
              <Input
                id="practice_id"
                name="practice_id"
                type="select"
                invalid={ formValidation.practice_id === false }
                onChange={ handleInputChange }
                disabled={ isLoading }
                value={ formData.practice_id }
              >
                {
                  prestaciones.modules && Array.isArray(prestaciones.modules)
                    ? prestaciones.modules.map(item => <option key={ item.id } value={ item.id }>{ item.name }</option>)
                    : null
                }
              </Input>
            </Col>
          </Row>
        </FormGroup>
        <FormGroup>
          <Row>
            <Col sm={6}>
              <Label for="sessions">Cantidad de Sesiones <Required /></Label>
              <Input
                id="sessions"
                name="sessions"
                type="number"
                min="1"
                invalid={ formValidation.sessions === false }
                onChange={ handleInputChange }
                disabled={ isLoading }
                value={ formData.sessions }
                required
              />
              {
                formValidation.sessions === false
                  ? <InputError message="La cantidad de sesiones tiene que ser al menos 1" />
                  : null
              }
            </Col>
          </Row>
        </FormGroup>
        <FormGroup>
          <div className="text-center">
            {
              authorization
                ? <span>Se autorizó la prestación con el número <strong>${ authorization }</strong></span>
                : <Button type="button" onClick={ handleAuthorization } disabled={ isLoading }>Autorizar</Button>
            }
          </div>
        </FormGroup>
      </Form>
      <AlertModal isOpen={ alertShow } message={ alertMessage } onClose={ alertCallback } />
      <ConfirmModal
        isOpen={ confirm }
        title={ confirmTitle }
        action={ confirmAction }
        onClose={ () => setConfirm(false) }
        onConfirm={ () => handleAuthorization() }
      />
    </>
  );
}
